import React, { useState, useRef, useEffect, KeyboardEvent } from "react";
import { Controller } from "@hotwired/stimulus";
import { createRoot } from "react-dom/client";
import {
  Send,
  Lightbulb,
  Scan,
  Rocket,
  Building2,
  CopyPlus,
  Milestone,
  Activity,
  Globe,
  type LucideIcon,
} from "lucide-react";
import { RegistrationModal } from "../lib/registration_modal";

const BADGE_ICONS: Record<string, LucideIcon> = {
  lightbulb: Lightbulb,
  scan: Scan,
  rocket: Rocket,
  "building-2": Building2,
  "copy-plus": CopyPlus,
  milestone: Milestone,
  activity: Activity,
  globe: Globe,
};

interface Badge {
  label: string;
  query: string;
  icon?: string;
}

interface ChatTeaserAppProps {
  registrationPath: string;
  badges: Badge[];
}

// Controlled version of MessageInput for the teaser
function TeaserInput({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!value.trim()) return;
    onSubmit();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="max-w-[700px] mx-auto flex relative z-[2]">
      <div className="w-full rounded-lg bg-slate-50 p-2 shadow-lg">
        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="flex flex-col relative">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Posez une question..."
              className="w-full min-h-[64px] max-h-[200px] resize-none rounded-lg border border-slate-300 px-3 py-2 pb-10 text-sm placeholder:text-slate-400 focus:border-default-500 focus:outline-none focus:ring-1 focus:ring-default-500"
              rows={2}
            />
            <div className="absolute right-4 bottom-2 flex items-center gap-2">
              <button
                type="submit"
                disabled={!value.trim()}
                className="inline-flex items-center gap-x-1.5 rounded-md font-medium px-3 py-2 text-sm bg-default-600 text-white hover:bg-default-600/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="size-4" />
                <span>Envoyer</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function DemoBadges({
  badges,
  onBadgeClick,
}: {
  badges: Badge[];
  onBadgeClick: (query: string) => void;
}) {
  if (!badges.length) return null;

  return (
    <div className="flex flex-col items-center gap-3 mt-6">
      <p className="text-sm text-slate-600 font-medium py-4">Commencez avec</p>
      <div className="flex flex-wrap justify-center gap-2">
        {badges.map((badge) => {
          const IconComp = badge.icon ? BADGE_ICONS[badge.icon] : null;
          return (
            <button
              key={badge.label}
              type="button"
              onClick={() => onBadgeClick(badge.query)}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full bg-white border border-default-200 text-default-700 hover:bg-default-50 hover:border-default-400 transition-colors shadow-sm"
            >
              {IconComp && <IconComp className="w-3.5 h-3.5 text-default-500" />}
              {badge.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ChatTeaserApp({ registrationPath, badges }: ChatTeaserAppProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const handleSubmit = () => {
    if (inputValue.trim()) {
      setIsModalOpen(true);
    }
  };

  const handleBadgeClick = (query: string) => {
    setInputValue(query);
    setIsModalOpen(true);
  };

  return (
    <>
      <TeaserInput
        value={inputValue}
        onChange={setInputValue}
        onSubmit={handleSubmit}
      />
      <DemoBadges badges={badges} onBadgeClick={handleBadgeClick} />
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        registrationPath={registrationPath}
      />
    </>
  );
}

export default class extends Controller {
  static values = {
    registrationPath: String,
    badges: Array,
  };

  declare registrationPathValue: string;
  declare badgesValue: Array<{ label: string; query: string }>;
  private root: ReturnType<typeof createRoot> | null = null;

  connect() {
    document.addEventListener("turbo:before-cache", this.beforeCache);

    if (!this.root) {
      this.root = createRoot(this.element as HTMLElement);
      this.root.render(
        <ChatTeaserApp
          registrationPath={this.registrationPathValue}
          badges={this.badgesValue || []}
        />,
      );
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache);

    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  beforeCache = () => {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  };
}
